import React from "react";
import { User as UserIcon, QrCode, Star, Dot } from "lucide-react";
import { translateFr as tfr } from "@/i18n";

import { dateLocale } from "@/lib/utils";
const dash = (v) => (v === 0 ? "0" : v ? String(v) : "—");

export const negotiationDocRef = (playerId) => {
  const tail = String(playerId || "").replace(/[^a-zA-Z0-9]/g, "").slice(-5).toUpperCase();
  return tail ? `ATL-NP-${new Date().getFullYear()}-${tail}` : null;
};

/**
 * A4Preview — single shared renderer for the negotiation sheet (matches the PDF
 * template schema). Renders a real empty template until data arrives — no fake data.
 */
export const A4Preview = React.forwardRef(function A4Preview(
  { player, sportLabel, identity = {}, form = {}, statFields = [], agent = {}, sections = {} },
  ref
) {
  const sd = player?.sport_data || {};
  const iv = (k) => identity[k] ?? player?.[k] ?? sd[k] ?? null;
  const stats = form.season_stats || {};
  const sec = { profile: true, contract: true, market_value: true, career: true, agent_contact: true, qr_code: true, ...sections };
  const strengths = String(form.strengths || "").split("\n").filter((s) => s.trim());
  const notes = String(form.notes || "").split("\n").filter((s) => s.trim());

  const idLeft = [
    [tfr("ÂGE"), iv("age") ? `${iv("age")} ans` : null],
    [tfr("NATIONALITÉ"), iv("nationality")],
    ["PIED DOMINANT", iv("foot") || sd.dominant_hand],
    ["TAILLE", iv("height_cm") ? `${iv("height_cm")} cm` : null],
    ["POIDS", iv("weight_kg") ? `${iv("weight_kg")} kg` : null],
  ];
  const idRight = [
    ["CLUB ACTUEL", iv("club")],
    [tfr("FIN DE CONTRAT"), form.contract_end_date],
    [tfr("MARCHÉ PRÉFÉRÉ"), form.preferred_market],
    [tfr("STATUT"), player?.status],
    ["NIVEAU", player?.level],
  ];

  return (
    <div
      ref={ref}
      className="w-full aspect-[210/297] overflow-hidden bg-white text-[#111827] shadow-[0_12px_32px_rgba(0,0,0,0.28)] rounded-sm select-none"
      role="img"
      aria-label={`Aperçu de la fiche de négociation${player?.name ? ` de ${player.name}` : ""}`}
      data-testid="negotiation-a4-preview"
    >
      <div className="h-full flex flex-col px-[4.5%] py-[3.5%] text-[clamp(4px,1.05vw,9px)] leading-tight">
        {/* Header — document identity first (athlete-first hierarchy, no brand wordmark) */}
        <div className="border-b-2 border-[#E11D2E] pb-[1.5%]">
          <div className="font-bold text-[1.45em] tracking-tight">{tfr("FICHE ATHLÈTE / NÉGOCIATION")}</div>
          <div className="text-[0.85em] text-gray-500">{tfr("PROFIL IMPRIMABLE POUR NÉGOCIATIONS ET PRÉSENTATIONS")}</div>
        </div>

        {/* Identity */}
        <div className="flex gap-[3%] mt-[2.5%]">
          <div className="w-[22%] shrink-0">
            <div className="aspect-[5/6] bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden">
              {player?.photo_url ? (
                <img src={player.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-gray-400">
                  <UserIcon className="w-[38%] h-auto mx-auto opacity-40" strokeWidth={1} />
                  <div className="text-[0.8em] mt-1">PHOTO JOUEUR</div>
                </div>
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-[2.1em] uppercase truncate">{player?.name || tfr("Nom Prénom")}</div>
            <div className="text-[#E11D2E] font-bold text-[0.95em] uppercase mt-[0.5%]">
              {sportLabel || "Sport"} &nbsp;|&nbsp; {player?.position || "Poste"}
            </div>
            <div className="grid grid-cols-2 gap-x-[4%] mt-[2.5%]">
              {[idLeft, idRight].map((col, ci) => (
                <div key={ci} className="space-y-[3%]">
                  {col.map(([l, v]) => (
                    <div key={l} className="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-[1%]">
                      <span className="text-gray-400 text-[0.78em]">{l}</span>
                      <span className="font-semibold truncate">{dash(v)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key indicators */}
        <SectionTitle>Indicateurs clés {form.season_label || ""}</SectionTitle>
        <div className="grid grid-cols-6 border border-gray-200 divide-x divide-gray-200 bg-[#F8F9FB]">
          {statFields.slice(0, 6).map((f) => (
            <div key={f.key} className="py-[1.8%] px-1 text-center min-w-0">
              <div className="font-black text-[1.5em]">{dash(stats[f.key])}</div>
              <div className="text-gray-500 text-[0.68em] uppercase leading-[1.15] mt-[2%] truncate">{f.label}</div>
            </div>
          ))}
        </div>

        {/* Profile + contract */}
        <div className="grid grid-cols-2 gap-[4%] flex-1 min-h-0 mt-[1%]">
          <div className="min-w-0">
            {sec.profile && (
              <>
                <SectionTitle>{tfr("Profil athlète")}</SectionTitle>
                <div className="space-y-[2%]">
                  {(strengths.length ? strengths : [null, null, null]).slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <Star className="w-[1.1em] h-[1.1em] text-[#E11D2E] shrink-0 mt-[1%]" fill="currentColor" />
                      {s ? <span className="truncate">{s}</span> : <span className="border-b border-gray-200 flex-1 h-[1em]" />}
                    </div>
                  ))}
                  {form.styles_line && (
                    <div className="text-[0.9em]"><span className="font-bold">Style de jeu :</span> {form.styles_line}</div>
                  )}
                  {form.env_line && (
                    <div className="text-[0.9em]"><span className="font-bold">Environnement :</span> {form.env_line}</div>
                  )}
                  {form.summary && (
                    <div className="text-[0.88em] text-gray-600 leading-snug line-clamp-4 pt-[1%]">{form.summary}</div>
                  )}
                </div>
              </>
            )}
            {sec.career && (
              <>
                <SectionTitle>{tfr("Carrière & faits marquants")}</SectionTitle>
                <div className="space-y-[2%]">
                  {(notes.length ? notes : [null, null, null]).slice(0, 4).map((s, i) => (
                    <div key={i} className="flex items-start gap-1">
                      <Dot className="w-[1.4em] h-[1.4em] text-[#E11D2E] shrink-0" />
                      {s ? <span className="truncate">{s}</span> : <span className="border-b border-gray-200 flex-1 h-[1em] mt-[2%]" />}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="min-w-0">
            {sec.contract && (
              <>
                <SectionTitle>{tfr("Contrat & marché")}</SectionTitle>
                <div className="space-y-[2.5%]">
                  {[
                    [tfr("VALEUR MARCHANDE ESTIMÉE"), sec.market_value ? form.market_value : "Confidentiel"],
                    [tfr("PRÉTENTIONS SALARIALES"), sec.market_value ? form.salary_expectation : "Confidentiel"],
                    [tfr("INDEMNITÉ / CLAUSE"), form.transfer_fee],
                    [tfr("DISPONIBILITÉ"), form.availability],
                    [tfr("FIN DE CONTRAT"), form.contract_end_date],
                  ].map(([l, v]) => (
                    <div key={l} className="flex items-baseline justify-between gap-2 border-b border-gray-100 pb-[1%]">
                      <span className="text-gray-400 text-[0.78em]">{l}</span>
                      <span className="font-semibold truncate">{dash(v)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {sec.qr_code && (
              <>
                <SectionTitle>{tfr("Média & dossier complet")}</SectionTitle>
                <div className="flex items-center gap-[4%]">
                  <div className="w-[26%] aspect-square border border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                    <QrCode className="w-[55%] h-[55%]" strokeWidth={1.2} />
                  </div>
                  <div className="text-[0.78em] text-gray-500 leading-snug">
                    SCANNEZ POUR ACCÉDER<br />
                    ✓ Vidéos highlights<br />{tfr("✓ Dossier complet (PDF)")}<br />✓ Stats détaillées
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Agent footer */}
        <div className="mt-auto border-t-2 border-[#E11D2E] pt-[1.5%] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-[2.2em] w-[2.2em] rounded-full bg-[#E11D2E] text-white font-black text-[1em] flex items-center justify-center shrink-0">
              {(agent.name || "A").trim()[0]?.toUpperCase() || "A"}
            </div>
            <div className="min-w-0">
              <div className="text-gray-400 text-[0.7em]">{tfr("VOTRE CONTACT AGENT")}</div>
              <div className="font-bold truncate">{sec.agent_contact ? dash(agent.name) : "—"}</div>
              <div className="text-gray-500 text-[0.75em] truncate">{sec.agent_contact ? (agent.role || "Agent") : "—"}</div>
            </div>
          </div>
          <div className="text-right text-[0.75em] text-gray-500 min-w-0">
            <div className="truncate">{sec.agent_contact ? dash(agent.email) : "—"}</div>
            <div>MAJ : {new Date().toLocaleDateString(dateLocale())}</div>
            {negotiationDocRef(player?.id) && <div>Réf. {negotiationDocRef(player.id)}</div>}
          </div>
        </div>
        <div className="flex items-center justify-center gap-[0.45em] text-gray-400 text-[0.7em] mt-[1%]" data-testid="generated-with-athlynx">
          <img src="/athlynx-mark-red.svg" alt="" className="h-[1.1em] w-auto shrink-0" />
          <span>{tfr("Généré avec Athlynx")}</span>
        </div>
      </div>
    </div>
  );
});

function SectionTitle({ children }) {
  return (
    <div className="mt-[2.2%] mb-[1.2%]">
      <div className="text-[#E11D2E] font-bold text-[0.85em] uppercase tracking-wide">{children}</div>
      <div className="h-px bg-gray-900 mt-[0.5%]" />
    </div>
  );
}

export default A4Preview;
